import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { StoreLoader, Unsubscriber } from '@core-lib/classes';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, map, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { SharedStoreSelectors } from '@core-lib/store/shared-store';
import { PageService } from '@core-lib/services/page.service';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';

@Component({
    selector: 'app-base',
    template: '',
    standalone: false
})
export class AppBaseComponent extends Unsubscriber implements OnInit {
	protected routeSnapshot: ActivatedRouteSnapshot;
	layoutName = '';

	appName: string = 'BackOffice';

	newVersionAvailable: boolean = false;

	referentials$ = this.store$.select(SharedStoreSelectors.selectReferentials);
	referentialsLoader = new StoreLoader(
		this.store$.select(SharedStoreSelectors.selectReferentialsIsLoading),
		this.store$.select(SharedStoreSelectors.selectReferentialsError)
	);

	constructor(
		protected store$: Store,
		protected translateService: TranslateService,
		protected pageService: PageService,
		protected route: ActivatedRoute,
		protected router: Router,
		protected titleService: Title,
		protected swUpdate: SwUpdate
	) {
		super();

		this.translateService.onLangChange.pipe(takeUntil(this.destroySubscriptions$)).subscribe(_ => {
			this.setTitle();
		});
	}

	ngOnInit(): void {
		this.checkForUpdates();

		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				map(_ => this.findCurrentPageComponent()),
				distinctUntilChanged(),
				takeUntil(this.destroySubscriptions$)
			)
			.subscribe(route => {
				this.routeSnapshot = route;
				this.setLayout();
				const title = route.data['title'];
				const appN = route.data['appName'] === undefined ? this.appName : route.data['appName'];
				this.titleService.setTitle(`${appN} - ${title}`);
			});
	}

	checkForUpdates() {
		this.swUpdate.versionUpdates.pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe(evt => {
			this.newVersionAvailable = true;
		});
	}

	acceptNewVersion() {
		if (this.newVersionAvailable) {
			// Reload the page to update to the latest version.
			document.location.reload();
		}
	}

	promptUser(evt: VersionReadyEvent): boolean {
		return window.confirm('Nouvelle version disponible. Charger la nouvelle version ?');
	}

	findCurrentPageComponent(): ActivatedRouteSnapshot {
		let inner = this.route.snapshot;
		while (inner.children.length !== 0) {
			inner = inner.children[0];
			this.layoutName = inner.data['layoutName'];
		}
		return inner;
	}

	setLayout(): void {
		this.setTitle();

		this.layoutName = this.routeSnapshot.data['layoutName'];
	}

	setTitle(): void {
		if (!this.routeSnapshot || !this.routeSnapshot.data['titleTranslationKey']) {
			return;
		}
		this.pageService.setTitle(this.routeSnapshot.data['titleTranslationKey'], this.routeSnapshot.params);
	}
}
